import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatRadioModule } from "@angular/material/radio";
import {
  createEmptyForm,
  createQuestion,
  GUIDE_QUESTION_LIMITS,
  GUIDE_QUESTION_TYPE_LABELS,
  GuideQuestion,
  GuideQuestionForm,
  GuideQuestionType,
  guideQuestionFormErrors,
  newQuestionId,
  parseGuideQuestionForm,
  questionHasOptions,
  trueFalseOptions,
  yesNoOptions,
} from "src/app/models/guide-question-form.model";

/**
 * Authoring UI for a step or end-of-guide question form. Emits the working copy on every change;
 * callers run `cleanGuideQuestionForm` before saving.
 */
@Component({
  selector: "app-guide-question-form-editor",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
  ],
  templateUrl: "./guide-question-form-editor.component.html",
  styleUrls: ["./guide-question-form-editor.component.scss"],
})
export class GuideQuestionFormEditorComponent implements OnChanges {
  @Input() form: GuideQuestionForm | null | undefined;
  @Input() heading = "Ask questions";
  @Input() hint = "Show a short form before the viewer moves on.";
  @Input() disabled = false;
  @Output() formChange = new EventEmitter<GuideQuestionForm | null>();

  model: GuideQuestionForm | null = null;
  private lastEmitted: GuideQuestionForm | null = null;

  readonly limits = GUIDE_QUESTION_LIMITS;
  readonly typeOptions = (Object.keys(GUIDE_QUESTION_TYPE_LABELS) as GuideQuestionType[]).map((value) => ({
    value,
    label: GUIDE_QUESTION_TYPE_LABELS[value],
  }));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["form"] && this.form !== this.lastEmitted) {
      this.model = parseGuideQuestionForm(this.form);
    }
  }

  get enabled(): boolean {
    return !!this.model?.enabled;
  }

  get errors(): string[] {
    return guideQuestionFormErrors(this.model);
  }

  setEnabled(on: boolean): void {
    if (on) {
      if (!this.model) this.model = createEmptyForm();
      else {
        this.model.enabled = true;
        if (!this.model.questions.length) this.model.questions.push(createQuestion());
      }
    } else if (this.model) {
      this.model.enabled = false;
    }
    this.emit();
  }

  setMode(mode: "feedback" | "score"): void {
    if (!this.model) return;
    this.model.mode = mode;
    this.emit();
  }

  addQuestion(): void {
    if (!this.model || this.model.questions.length >= this.limits.maxQuestions) return;
    this.model.questions.push(createQuestion());
    this.emit();
  }

  removeQuestion(index: number): void {
    if (!this.model) return;
    this.model.questions.splice(index, 1);
    this.emit();
  }

  moveQuestion(index: number, delta: number): void {
    if (!this.model) return;
    const target = index + delta;
    const list = this.model.questions;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    this.emit();
  }

  hasOptions(q: GuideQuestion): boolean {
    return questionHasOptions(q.type);
  }

  showAccepted(q: GuideQuestion): boolean {
    if (q.type === "multiple") return this.model?.mode === "score";
    return q.type === "single" || q.type === "yesNo" || q.type === "trueFalse";
  }

  changeType(q: GuideQuestion, type: GuideQuestionType): void {
    const previous = q.type;
    q.type = type;
    q.acceptedOptionId = null;
    if (!questionHasOptions(type)) {
      q.options = null;
    } else if (type === "trueFalse") {
      q.options = trueFalseOptions();
    } else if (type === "yesNo") {
      q.options = yesNoOptions();
    } else {
      if (previous === "trueFalse" || previous === "yesNo" || previous === "longText" || previous === "text" || previous === "number" || previous === "confirmation" || previous === "photo" || !q.options?.length) {
        q.options = [
          { id: newQuestionId(), text: "" },
          { id: newQuestionId(), text: "" },
        ];
      }
      if (type === "single") this.keepSingleCorrect(q);
    }
    this.emit();
  }

  addOption(q: GuideQuestion): void {
    if (!q.options || q.options.length >= this.limits.maxOptions) return;
    q.options.push({ id: newQuestionId(), text: "" });
    this.emit();
  }

  removeOption(q: GuideQuestion, index: number): void {
    if (!q.options || q.options.length <= this.limits.minOptions) return;
    q.options.splice(index, 1);
    this.emit();
  }

  toggleCorrect(q: GuideQuestion, optionId: string): void {
    if (!q.options) return;
    if (q.type === "multiple") {
      const opt = q.options.find((o) => o.id === optionId);
      if (opt) opt.isCorrect = opt.isCorrect ? null : true;
      q.acceptedOptionId = null;
    } else {
      q.options.forEach((o) => (o.isCorrect = o.id === optionId ? true : null));
      q.acceptedOptionId = optionId;
    }
    this.emit();
  }

  accepted(q: GuideQuestion, optionId: string): boolean {
    return q.acceptedOptionId === optionId || !!(q.options || []).find((o) => o.id === optionId && o.isCorrect);
  }

  emit(): void {
    this.lastEmitted = this.model;
    this.formChange.emit(this.model);
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  private keepSingleCorrect(q: GuideQuestion): void {
    let seen = false;
    (q.options || []).forEach((o) => {
      if (o.isCorrect && !seen) seen = true;
      else o.isCorrect = null;
    });
  }
}
